import {useMemo, useState} from 'react';
import {Breadcrumb, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {ActivitySetUp} from '@/containers';
import {isNotEmptyAndDefined} from '@/helpers/shadcn/utils';
import {useAppSelector} from '@/store';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPES_ENUM,
  isVideoActivityType
} from '@/types/Activity';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import {COUNTRY_CODES_FREQUENCY} from '@/utils/constants/globalConstants';
import {DATE_VALIDATION_TYPE} from '@/utils/constants/commonConstants';
import {
  isInProgress,
  isPublished,
  flattenSegments,
  getCurrencySymbol
} from '@/utils/commons';
import {getModal} from '@/utils/CommonModal';
import {useDatePickerInput} from '@/components/atoms/DatePickerInput/datePickerInput-hook';
const AddActivity = () => {
  const [isCancel, setIsCancel] = useState(false);
  const {formData} = useSelector((state) => state.activity);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const isCountryCodeFrequency = useMemo(
    () => COUNTRY_CODES_FREQUENCY.includes(countryCode),
    [countryCode]
  );
  const localCurrencyCode = getCurrencySymbol(countryCode);
  const {
    handleDraft: handleReviewDraft,
    handleValidationModalClose,
    isNameValid,
    getActivityName,
    validateName,
    handleActivityDurationValidation,
    resetValidation,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity
  } = useActivityReview();

  const {
    pointsEarned,
    pointsEarnedValid,
    activityType,
    hasEndDate,
    startDate,
    endDate,
    dailyLoginLimit,
    dailyLoginLimitValid,
    allTimeLoginLimit,
    allTimeLoginLimitValid,
    startDateValid,
    endDateValid,
    activityName,
    orderType,
    localCurrency,
    localCurrencyValid,
    numberOfCases,
    numberOfCasesValid,
    receivePoints,
    status,
    pointsToRedeem,
    localCurrencyValue,
    pointsToRedeemValid,
    localCurrencyValueValid
  } = formData;

  const isPublishedOrInProgress = isPublished(status) || isInProgress(status);
  const {validateDates: validateStartDate} = useDatePickerInput('startDate', [
    {type: 'required', msg: ` is required`},
    {
      type: DATE_VALIDATION_TYPE.START_DATE,
      msg: ` can't be in the past.`,
      endDate: endDate
    },
    {
      type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
      msg: `  Target can only start from a date in future`
    }
  ]);
  const {validateDates: validateEndDate} = useDatePickerInput('endDate', [
    {type: 'required', msg: ` is required`},
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`},
    {
      type: DATE_VALIDATION_TYPE.END_DATE,
      msg: ` can't be less than Start date.`,
      startDate: startDate
    }
  ]);

  const surveyEndDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.END_DATE,
        msg: 'End Date cannot be earlier than Start Date.',
        startDate,
        excludeLabel: true
      }
    ],
    [startDate]
  );
  const {validateDates: validateSurveyEndDate} = useDatePickerInput(
    'Active End Date *',
    surveyEndDateRules
  );

  const surveyStartDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.START_DATE,
        msg: ` can't be in the past.`,
        endDate
      },
      {
        type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
        msg: `  Target can only start from a date in future`
      }
    ],
    [endDate]
  );
  const {validateDates: validateSurveyStartDate} = useDatePickerInput(
    'Active Start Date *',
    surveyStartDateRules
  );
  // Order alignment for Welcome review page
  const isValidStartDate = (date) => {
    if (!date) return false;
    const d = validateStartDate(date);
    return d === undefined || d === null;
  };

  const isValidEndDate = (date) => {
    if (!date) return false;
    const d = validateEndDate(date);
    return d === undefined || d === null;
  };

  const isValidSurveyStartDate = (date) => {
    if (!date) return false;
    const d = validateSurveyStartDate(date);
    return d === undefined || d === null;
  };

  const isValidSurveyEndDate = (date) => {
    if (!date) return false;
    const d = validateSurveyEndDate(date);
    return d === undefined || d === null;
  };

  const videoEndDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.END_DATE,
        msg: 'End Date cannot be earlier than Start Date.',
        startDate,
        excludeLabel: true
      }
    ],
    [startDate]
  );
  const {validateDates: validateVideoEndDate} = useDatePickerInput(
    'Video Active End Date *',
    videoEndDateRules
  );

  const videoStartDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.START_DATE,
        msg: ` can't be in the past.`,
        endDate
      },
      {
        type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
        msg: `  Target can only start from a date in future`
      }
    ],
    [endDate]
  );
  const {validateDates: validateVideoStartDate} = useDatePickerInput(
    'Video Active Start Date *',
    videoStartDateRules
  );

  const isValidVideoStartDate = (date) => {
    if (!date) return false;
    const d = validateVideoStartDate(date);
    return d === undefined || d === null;
  };

  const isValidVideoEndDate = (date) => {
    if (!date) return false;
    const d = validateVideoEndDate(date);
    return d === undefined || d === null;
  };

  const isSurveyActivity = activityType === ACTIVITY_TYPE.SURVEY;
  const isVideoActivity = isVideoActivityType(activityType);
  const isThreeStepActivity = isSurveyActivity || isVideoActivity;
  const welcomeActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
    ...(hasEndDate && {endDate: endDate})
  };

  // Order alignment for Login review page
  const loginActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
    ...(allTimeLoginLimit && {allTimeLoginLimit: allTimeLoginLimit}),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  };

  const pointsOnOrderDeliveryActivityOrder = () => {
    const fieldsArranged = {
      activityType: activityType,
      activityName: activityName,
      orderType: orderType,
      ...(formData.name && {name: formData.name}),
      ...(formData.uuid && {uuid: formData.uuid}),
      ...(formData.status && {status: formData.status}),
      ...(formData.id && {id: formData.id})
    };
    if (isCountryCodeFrequency) {
      fieldsArranged.numberOfCases = numberOfCases;
      fieldsArranged.pointsEarned = pointsEarned;
    } else {
      fieldsArranged.localCurrency = localCurrency;
      fieldsArranged.pointsEarned = pointsEarned;
    }
    fieldsArranged.startDate = startDate;
    fieldsArranged.endDate = endDate;
    fieldsArranged.hasEndDate = true;
    return fieldsArranged;
  };

  const handleDraft = (event) => {
    event.stopPropagation();
    handleReviewDraft();
  };

  const handleNext = async () => {
    const uniqueActivityName = getActivityName();
    // validate name only if it's a new activity as edting name is disabled
    // Skip name validation for Currency to Points (auto-generated name)
    if (
      formData?.status == null &&
      activityType !== ACTIVITY_TYPE.CURRENCY_TO_POINTS
    ) {
      const isValidName = await validateName(uniqueActivityName);
      if (!isValidName) return;
    }

    // Check if Point Expiration data is valid before proceeding
    if (
      activityType === ACTIVITY_TYPE.POINT_EXPIRATION &&
      isInvalidPointExpirationData()
    ) {
      // Show validation error
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Please enter a valid frequency between 1 and 365 days',
          type: 'error'
        })
      );
      return;
    }

    const formDataWithOrder = getFormDataWithOrder();
    dispatch(setActivityFormData(formDataWithOrder));

    // Skip validation for Point Expiration and Currency to Points since API doesn't support it yet
    if (
      activityType !== ACTIVITY_TYPE.POINT_EXPIRATION &&
      activityType !== ACTIVITY_TYPE.CURRENCY_TO_POINTS
    ) {
      let validationParams = {...formData, activityName: uniqueActivityName};
      const isDurationValid =
        await handleActivityDurationValidation(validationParams);
      if (!isDurationValid) {
        // Validation failed, toast is already shown by the hook
        return;
      }
    }

    // Validation passed - reset any existing toast state and navigate
    resetValidation();
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      navigate(ROUTES.ACTIVITY_ATTRIBUTES);
    } else if (isVideoActivity) {
      navigate(ROUTES.ACTIVITY_DISPLAY);
    } else {
      navigate(ROUTES.ACTIVITY_REVIEW);
    }
  };
  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidLoginDataForEdit = ({validateStartDate = true}) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate);
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isReceivePointsInvalid = !(
      typeof receivePoints === 'string' && isNotEmptyAndDefined(receivePoints)
    );
    const isDailyLoginLimitInvalid = !isNotEmptyAndDefined(dailyLoginLimit);

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true;
    const hasDailyLoginLimitError = dailyLoginLimitValid === true;
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true;
    const validEndDate = isValidEndDate(endDate);
    return (
      !validStartDate ||
      hasPointsEarnedError ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate ||
      isDailyLoginLimitInvalid
    );
  };

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidLoginDataForCreation = ({validateStartDate = true}) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate);
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isReceivePointsInvalid = !(
      typeof receivePoints === 'string' && isNotEmptyAndDefined(receivePoints)
    );
    const isDailyLoginLimitInvalid = !isNotEmptyAndDefined(dailyLoginLimit);

    // Stricter validation - check for validation errors AND undefined states
    const hasPointsEarnedError = pointsEarnedValid === true;
    const hasDailyLoginLimitError = dailyLoginLimitValid === true;
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true;
    const validEndDate = isValidEndDate(endDate);
    return (
      hasPointsEarnedError ||
      !validStartDate ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate ||
      isDailyLoginLimitInvalid
    );
  };

  const isInvalidLoginData = ({validateStartDate = true}) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode =
      formData.uuid || formData.status || formData.activityName;
    if (isEditMode) {
      return isInvalidLoginDataForEdit({validateStartDate});
    } else {
      return isInvalidLoginDataForCreation({validateStartDate});
    }
  };

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidWelcomeDataForEdit = ({validateStartDate = true}) => {
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;
    const hasStartDateError = startDateValid === true;
    const hasEndDateError = endDateValid === true;

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate);
      return (
        !validStartDate ||
        hasPointsError ||
        hasStartDateError ||
        hasEndDateError ||
        isStartDateMissing ||
        isEndDateMissing
      );
    }

    return (
      !validStartDate ||
      hasPointsError ||
      hasStartDateError ||
      isStartDateMissing
    );
  };

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidWelcomeDataForCreation = ({validateStartDate = true}) => {
    const isStartDateInvalid = validateStartDate
      ? validateDate(startDateValid)
      : true;
    const isEndDateInvalid = validateDate(endDateValid);
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);

    // Check if points earned is invalid (either validation error or empty value)
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate);
      return (
        hasPointsError ||
        isStartDateInvalid ||
        isEndDateInvalid ||
        isStartDateMissing ||
        isEndDateMissing
      );
    }

    return hasPointsError || isStartDateInvalid || isStartDateMissing;
  };

  const isInvalidWelcomeData = ({validateStartDate = true}) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode =
      formData.activityName || formData.uuid || formData.status;

    if (isEditMode) {
      return isInvalidWelcomeDataForEdit({validateStartDate});
    } else {
      return isInvalidWelcomeDataForCreation({validateStartDate});
    }
  };

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidPointsOrderDeliverForEdit = ({validateStartDate = true}) => {
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    const isEndDateMissing = !isNotEmptyAndDefined(endDate);
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid === true || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid === true || !isNotEmptyAndDefined(localCurrency);

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true;
    const hasStartDateError = startDateValid === true;
    const hasEndDateError = endDateValid === true;

    return (
      hasPointsEarnedError ||
      !validStartDate ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      hasStartDateError ||
      hasEndDateError ||
      isStartDateMissing ||
      isEndDateMissing ||
      !isNotEmptyAndDefined(orderType)
    );
  };

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidPointsOrderDeliverForCreation = ({
    validateStartDate = true
  }) => {
    const isStartDateInvalid = validateStartDate
      ? validateDate(startDateValid)
      : true;
    const isEndDateInvalid =
      !isStartDateInvalid && formData.endDate === undefined;
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid || !isNotEmptyAndDefined(localCurrency);
    const hasPointsEarnedError = pointsEarnedValid === true;
    return (
      hasPointsEarnedError ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      !isNotEmptyAndDefined(orderType)
    );
  };

  const isInvalidPointsOrderDeliver = ({validateStartDate = true}) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode =
      formData.activityName || formData.uuid || formData.status;

    if (isEditMode) {
      return isInvalidPointsOrderDeliverForEdit({validateStartDate});
    } else {
      return isInvalidPointsOrderDeliverForCreation({validateStartDate});
    }
  };

  const validateDate = (dateValid) => {
    // Only return true (invalid) if there's an actual validation error
    // undefined means not validated yet, which should be treated as valid for pre-filled data
    return dateValid === true;
  };

  // Validation for Point Expiration activity
  const isInvalidPointExpirationData = () => {
    const isNoOfDaysInvalid = !isNotEmptyAndDefined(formData?.noOfDays);

    // Check if noOfDays is within valid range (1-365)
    const noOfDaysValue = parseInt(formData?.noOfDays, 10);
    const isNoOfDaysOutOfRange =
      isNaN(noOfDaysValue) || noOfDaysValue < 1 || noOfDaysValue > 365;

    return isNoOfDaysInvalid || isNoOfDaysOutOfRange;
  };

  // Validation for Pay by Points on Order Delivery activity
  const isInvalidPayByPointsDataForEdit = ({validateStartDate = true}) => {
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    const isEndDateMissingWhenRequired =
      hasEndDate && !isNotEmptyAndDefined(endDate);
    const isEndDateInvalidWhenRequired =
      hasEndDate && endDate && !isValidEndDate(endDate);
    const hasPointsToRedeemError = pointsToRedeemValid === true;
    const hasLocalCurrencyValueError = localCurrencyValueValid === true;
    const hasStartDateError = startDateValid === true;
    const hasEndDateError = hasEndDate && endDateValid === true;
    return (
      hasPointsToRedeemError ||
      hasLocalCurrencyValueError ||
      !validStartDate ||
      !isNotEmptyAndDefined(pointsToRedeem) ||
      !isNotEmptyAndDefined(localCurrencyValue) ||
      !isNotEmptyAndDefined(activityName) ||
      hasStartDateError ||
      hasEndDateError ||
      isStartDateMissing ||
      isEndDateMissingWhenRequired ||
      isEndDateInvalidWhenRequired
    );
  };

  const isInvalidPayByPointsDataForCreation = ({validateStartDate = true}) => {
    const isStartDateInvalid = validateStartDate
      ? validateDate(startDateValid)
      : true;
    const isEndDateInvalidWhenRequired =
      hasEndDate &&
      (!isNotEmptyAndDefined(endDate) || validateDate(endDateValid));
    const hasPointsToRedeemError = pointsToRedeemValid === true;
    const hasLocalCurrencyValueError = localCurrencyValueValid === true;
    const isPointsToRedeemInvalid = !isNotEmptyAndDefined(pointsToRedeem);
    const isLocalCurrencyValueInvalid =
      !isNotEmptyAndDefined(localCurrencyValue);
    const isActivityNameInvalid = !isNotEmptyAndDefined(activityName);
    return (
      hasPointsToRedeemError ||
      hasLocalCurrencyValueError ||
      isPointsToRedeemInvalid ||
      isLocalCurrencyValueInvalid ||
      isActivityNameInvalid ||
      isStartDateInvalid ||
      isEndDateInvalidWhenRequired
    );
  };

  const isInvalidPayByPointsData = ({validateStartDate = true}) => {
    const isEditMode =
      formData.activityName || formData.uuid || formData.status;
    if (isEditMode) {
      return isInvalidPayByPointsDataForEdit({validateStartDate});
    }
    return isInvalidPayByPointsDataForCreation({validateStartDate});
  };

  // Validation for Currency to Points activity
  const isInvalidCurrencyToPointsDataForEdit = ({validateStartDate = true}) => {
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);
    const validStartDate = validateStartDate
      ? isValidStartDate(startDate)
      : true;
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isLocalCurrencyInvalid = !isNotEmptyAndDefined(localCurrency);
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;
    const hasStartDateError = startDateValid === true;
    const hasEndDateError = endDateValid === true;

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate);
      return (
        !validStartDate ||
        hasPointsError ||
        hasStartDateError ||
        hasEndDateError ||
        isStartDateMissing ||
        isEndDateMissing ||
        isLocalCurrencyInvalid
      );
    }
    return (
      !validStartDate ||
      hasPointsError ||
      hasStartDateError ||
      isStartDateMissing ||
      isLocalCurrencyInvalid
    );
  };

  const isInvalidCurrencyToPointsDataForCreation = ({
    validateStartDate = true
  }) => {
    const isStartDateInvalid = validateStartDate
      ? validateDate(startDateValid)
      : true;
    const isStartDateMissing = !isNotEmptyAndDefined(startDate);
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned);
    const isLocalCurrencyInvalid = !isNotEmptyAndDefined(localCurrency);
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid;

    if (hasEndDate) {
      const isEndDateInvalid = validateDate(endDateValid);
      const isEndDateMissing = !isNotEmptyAndDefined(endDate);
      return (
        isStartDateInvalid ||
        isEndDateInvalid ||
        isStartDateMissing ||
        isEndDateMissing ||
        hasPointsError ||
        isLocalCurrencyInvalid
      );
    }
    return (
      isStartDateInvalid ||
      isStartDateMissing ||
      hasPointsError ||
      isLocalCurrencyInvalid
    );
  };

  const isInvalidCurrencyToPointsData = ({validateStartDate = true}) => {
    const isEditMode = formData.uuid || formData.status;
    if (isEditMode) {
      return isInvalidCurrencyToPointsDataForEdit({validateStartDate});
    }
    return isInvalidCurrencyToPointsDataForCreation({validateStartDate});
  };

  const isInvalidVideoData = ({validateStartDate = true}) => {
    const isEditMode =
      formData.activityName || formData.uuid || formData.status;
    const nameInvalid =
      !isNotEmptyAndDefined(activityName) || String(activityName).trim() === '';
    const pointsInvalid =
      !isNotEmptyAndDefined(pointsEarned) || pointsEarnedValid === true;
    const startMissing = !isNotEmptyAndDefined(startDate);
    const endMissing = !isNotEmptyAndDefined(endDate);
    const validVideoStart = validateStartDate
      ? isValidVideoStartDate(startDate)
      : true;
    const validVideoEnd = isValidVideoEndDate(endDate);
    const hasStartFieldError = startDateValid === true;
    const hasEndFieldError = endDateValid === true;
    const hasAllTimeError = allTimeLoginLimitValid === true;
    const allTimeEmpty = !isNotEmptyAndDefined(allTimeLoginLimit);
    const allTimeNum = Number(String(allTimeLoginLimit ?? '').trim());
    const allTimeNotPositive =
      !allTimeEmpty &&
      (!Number.isFinite(allTimeNum) ||
        allTimeNum <= 0 ||
        !Number.isInteger(allTimeNum));

    if (isEditMode) {
      return (
        nameInvalid ||
        pointsInvalid ||
        startMissing ||
        endMissing ||
        !validVideoStart ||
        !validVideoEnd ||
        hasStartFieldError ||
        hasEndFieldError ||
        allTimeEmpty ||
        hasAllTimeError ||
        allTimeNotPositive
      );
    }

    return (
      nameInvalid ||
      pointsInvalid ||
      startMissing ||
      endMissing ||
      !validVideoStart ||
      !validVideoEnd ||
      validateDate(startDateValid) ||
      validateDate(endDateValid) ||
      allTimeEmpty ||
      hasAllTimeError ||
      allTimeNotPositive
    );
  };

  const isInvalidSurveyData = ({validateStartDate = true}) => {
    const isEditMode =
      formData.activityName || formData.uuid || formData.status;
    const nameInvalid =
      !isNotEmptyAndDefined(activityName) || String(activityName).trim() === '';
    const pointsInvalid =
      !isNotEmptyAndDefined(pointsEarned) || pointsEarnedValid === true;
    const startMissing = !isNotEmptyAndDefined(startDate);
    const endMissing = !isNotEmptyAndDefined(endDate);
    const validSurveyStart = validateStartDate
      ? isValidSurveyStartDate(startDate)
      : true;
    const validSurveyEnd = isValidSurveyEndDate(endDate);
    const hasStartFieldError = startDateValid === true;
    const hasEndFieldError = endDateValid === true;
    const displayOrderMissing =
      !formData.displayFirst &&
      (formData.displayOrder === '' ||
        formData.displayOrder === null ||
        formData.displayOrder === undefined);

    if (isEditMode) {
      return (
        nameInvalid ||
        pointsInvalid ||
        startMissing ||
        endMissing ||
        !validSurveyStart ||
        !validSurveyEnd ||
        hasStartFieldError ||
        hasEndFieldError ||
        displayOrderMissing
      );
    }

    return (
      nameInvalid ||
      pointsInvalid ||
      startMissing ||
      endMissing ||
      !validSurveyStart ||
      !validSurveyEnd ||
      validateDate(startDateValid) ||
      validateDate(endDateValid) ||
      displayOrderMissing
    );
  };

  const validateFormData = ({validateStartDate = true}) => {
    const selectedSegs = formData?.selectedSegments ?? formData?.segments;
    const noSegmentsSelected =
      formData?.allCustomers === false &&
      (!Array.isArray(selectedSegs) ||
        flattenSegments(selectedSegs).length === 0);
    if (noSegmentsSelected) {
      return true;
    }
    if (
      activityType === ACTIVITY_TYPE.LOGIN ||
      activityType === ACTIVITY_TYPES_ENUM.LOGIN
    ) {
      return isInvalidLoginData({validateStartDate});
    } else if (
      activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityType === ACTIVITY_TYPES_ENUM.WELCOME
    ) {
      return isInvalidWelcomeData({validateStartDate});
    } else if (
      activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY ||
      activityType === ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY
    ) {
      return isInvalidPointsOrderDeliver({validateStartDate});
    } else if (
      activityType === ACTIVITY_TYPE.POINT_EXPIRATION ||
      activityType === ACTIVITY_TYPES_ENUM.POINT_EXPIRATION
    ) {
      return isInvalidPointExpirationData();
    } else if (
      activityType === ACTIVITY_TYPE.PAY_BY_POINTS ||
      activityType === ACTIVITY_TYPES_ENUM.PAY_BY_POINTS
    ) {
      return isInvalidPayByPointsData({validateStartDate});
    } else if (
      activityType === ACTIVITY_TYPE.SURVEY ||
      activityType === ACTIVITY_TYPES_ENUM.SURVEY
    ) {
      return isInvalidSurveyData({validateStartDate});
    } else if (isVideoActivityType(activityType)) {
      return isInvalidVideoData({validateStartDate});
    } else if (
      activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS ||
      activityType === ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS
    ) {
      return isInvalidCurrencyToPointsData({validateStartDate});
    } else if (handleHideSaveDraft()) {
      return true;
    }
  };

  const buildCommonFields = (fd) => ({
    name: fd.name,
    status: fd.status,
    uuid: fd.uuid,
    id: fd.id,
    ...(fd?.allCustomers === false &&
    Array.isArray(fd?.selectedSegments ?? fd?.segments) &&
    flattenSegments(fd.selectedSegments ?? fd.segments).length > 0
      ? {
          selectedSegments: fd.selectedSegments ?? fd.segments
        }
      : {}),
    allCustomers: fd?.allCustomers,
    segmentExclude: fd?.segmentExclude ?? fd?.excludeSegments,
    ...(Array.isArray(fd?.excludedSegments) && fd.excludedSegments.length > 0
      ? {excludedSegments: fd.excludedSegments}
      : {})
  });

  // Point Expiration activity order
  const pointExpirationActivityOrder = {
    activityType: activityType,
    noOfDays: formData.noOfDays,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  };

  // Pay by Points on Order Delivery activity order
  const payByPointsActivityOrder = () => ({
    activityType: activityType,
    activityName: activityName,
    pointsToRedeem: pointsToRedeem,
    localCurrencyValue: localCurrencyValue,
    startDate: startDate,
    ...(hasEndDate && {endDate: endDate}),
    hasEndDate: hasEndDate,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  });

  const surveyActivityOrder = () => {
    const ordered = {
      activityType,
      activityName,
      pointsEarned,
      startDate,
      endDate,
      ...(formData.name && {name: formData.name}),
      ...(formData.uuid && {uuid: formData.uuid}),
      ...(formData.status && {status: formData.status}),
      ...(formData.id && {id: formData.id})
    };
    ordered.displayFirst = !!formData.displayFirst;
    if (
      Array.isArray(formData.customerType) &&
      formData.customerType.length > 0
    ) {
      ordered.customerType = formData.customerType;
    }
    if (formData.url) {
      ordered.url = formData.url;
    }
    if (
      !formData.displayFirst &&
      formData.displayOrder !== '' &&
      formData.displayOrder !== undefined &&
      formData.displayOrder !== null
    ) {
      ordered.displayOrder = formData.displayOrder;
    }
    return ordered;
  };

  const videoActivityOrder = () => ({
    activityType,
    activityName,
    pointsEarned,
    startDate,
    endDate,
    allTimeLoginLimit:
      allTimeLoginLimit != null && allTimeLoginLimit !== ''
        ? String(allTimeLoginLimit)
        : allTimeLoginLimit,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  });

  // Currency to Points activity order
  const currencyToPointsActivityOrder = () => ({
    activityType: activityType,
    activityName: activityName,
    localCurrency: localCurrency,
    pointsEarned: pointsEarned,
    startDate: startDate,
    ...(hasEndDate && {endDate: endDate}),
    hasEndDate: hasEndDate,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  });

  const getFormDataWithOrder = () => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return {
        ...welcomeActivityOrder,
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return {
        ...loginActivityOrder,
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return {
        ...pointsOnOrderDeliveryActivityOrder(),
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return {
        ...pointExpirationActivityOrder,
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      return {
        ...payByPointsActivityOrder(),
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.SURVEY) {
      return {
        ...surveyActivityOrder(),
        ...buildCommonFields(formData)
      };
    } else if (isVideoActivityType(activityType)) {
      return {
        ...videoActivityOrder(),
        ...buildCommonFields(formData)
      };
    } else if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
      return {
        ...currencyToPointsActivityOrder(),
        ...buildCommonFields(formData)
      };
    }
  };

  const onClickPrimaryButton = () => {
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel);
  };

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false
    };
    dispatch(resetActivityData(defaultFormData));
  };

  const handleHideSaveDraft = () => {
    return formData.activityType === '' || formData.activityType === undefined;
  };

  const getCustomModal = (type) => {
    switch (type) {
      case 'CANCEL_MODAL':
        return getModal({
          title: 'Are you sure you want to cancel?',
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true,
          primaryButtonText: 'Yes',
          onClickPrimaryButton: onClickPrimaryButton,
          showSecondaryButton: true,
          secondaryButtonText: 'No',
          onClickSecondaryButton: onClickSecondaryButton,
          showModal: isCancel,
          onCloseModal: handleCancel,
          hideCloseButton: false
        });
      case 'NAME_VALIDATION_MODAL':
        return getModal({
          title: 'Duplicate Name Detected',
          text: (
            <>
              It looks like there's already an Activity with this name.
              <br />
              To avoid confusion, please enter a unique name before continuing.
            </>
          ),
          showPrimaryButton: true,
          primaryButtonText: 'Edit Name',
          onClickPrimaryButton: handleValidationModalClose,
          showSecondaryButton: false,
          showModal: !isNameValid,
          onCloseModal: handleValidationModalClose,
          hideCloseButton: true
        });
      default:
        return null;
    }
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              label: '',
              href: ROUTES.HOME
            },
            {
              label: 'Loyalty Campaigns',
              href: ROUTES.ACTIVITIES
            },
            {
              label: 'Add Activity',
              href: ROUTES.ACTIVITIES
            }
          ]
        }}
        id={'aa_bradcrumb'}
      />

      <PageHeader title={'Add Activity'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={isThreeStepActivity ? 3 : 2}
        titles={
          isSurveyActivity
            ? [{title: 'Setup'}, {title: 'Attributes'}, {title: 'Review'}]
            : isVideoActivity
              ? [{title: 'Setup'}, {title: 'Display'}, {title: 'Review'}]
              : [{title: 'Setup'}, {title: 'Review'}]
        }
        onStepClick={(step) => {
          if (isSurveyActivity) {
            if (step === 2) navigate(ROUTES.ACTIVITY_ATTRIBUTES);
            if (step === 3) navigate(ROUTES.ACTIVITY_REVIEW);
          } else if (isVideoActivity) {
            if (step === 2) navigate(ROUTES.ACTIVITY_DISPLAY);
            if (step === 3) navigate(ROUTES.ACTIVITY_REVIEW);
          } else if (step === 2) {
            navigate(ROUTES.ACTIVITY_REVIEW);
          }
        }}
      />
      <br />
      <ActivitySetUp
        localCurrencyCode={localCurrencyCode}
        showToast={showToast}
        toastMessage={toastMessage}
        handleToastClose={handleToastClose}
        handleViewActivity={handleViewActivity}
      />
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
      {isCancel && getCustomModal('CANCEL_MODAL')}
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={validateFormData({
          validateStartDate: !isPublishedOrInProgress
        })}
        hideSaveDraft={handleHideSaveDraft()}
        disableDraft={isPublishedOrInProgress}
      />
    </>
  );
};
export default AddActivity;
